/**
 * src/lib/placement.js — which printed sign did this guest scan?
 *
 * A code carries `?p=card-a`. That slug is permanent and deliberately
 * meaningless (`D-13`, sql/032): the sheet can be moved anywhere, and what
 * `card-a` MEANS is a row we edit after the sign is physically placed. So the
 * booth's only job is to notice the slug and carry it to the generator.
 *
 * ⚠️ IT HAS TO SURVIVE THE FIRST TAP. React Router does not preserve a query
 * string across navigation, and the booth moves through several steps before it
 * ever calls generate. Read once on arrival, keep it for the session.
 *
 * ⚠️ sessionStorage, NOT localStorage. A phone that scanned the gate sign in
 * the morning and the beer garden sign at four o'clock is two visits, and
 * localStorage would credit the second portrait to the first sign — quietly,
 * and forever. sessionStorage dies with the tab, which is the same lifetime as
 * a visit.
 *
 * ⚠️ SHAPE-CHECKED BECAUSE A GUEST CAN TYPE IT. Anyone can put anything after
 * `?p=`. It is written to a text column and never used to look anything up
 * (sql/032 explains why it is not a foreign key), so the blast radius of junk
 * is one wrong label on one row — but there is no reason to store 4 KB of
 * someone's idea of a joke, so it is bounded here.
 */
const KEY = 'st.placement';

/* Same shape as placement.slug in sql/032. Kept in step by hand; a slug that
   fails here is simply not recorded, never an error a guest can see. */
const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;

let memo = null;

/**
 * Read `?p=` from the current URL and remember it for this visit.
 * Safe to call on every render; the first valid slug of a visit wins.
 *
 * ⚠️ FIRST WINS, and that is a real decision. If a guest scans one sign, then
 * scans another before finishing, crediting the LAST would mean the sign that
 * actually started the visit loses the attribution to whichever one they walked
 * past afterwards. The first scan is the one that did the work.
 */
export function capturePlacement() {
  if (memo) return memo;
  try {
    const stored = sessionStorage.getItem(KEY);
    if (stored && SLUG_RE.test(stored)) { memo = stored; return memo; }
  } catch { /* private mode - fall through to the URL */ }

  try {
    const raw = new URLSearchParams(window.location.search).get('p');
    const slug = String(raw || '').trim().toLowerCase();
    if (!slug || !SLUG_RE.test(slug)) return null;
    memo = slug;
    try { sessionStorage.setItem(KEY, slug); } catch { /* memory only */ }
    return memo;
  } catch {
    return null;
  }
}

/** The slug for this visit, or null. Never throws. */
export function getPlacement() {
  return memo || capturePlacement();
}
